import { DropdownOptions } from '@components/common/formControl/dropdown';

export interface FormateDate {
	start: Date;
	end: Date;
}

export const formatDate = (date: string): string | null => {
	if (!date) return null;

	const d = new Date(date);

	if (isNaN(d.getTime())) return null;

	const year = d.getFullYear();
	const month = String(d.getMonth() + 1).padStart(2, '0');
	const day = String(d.getDate()).padStart(2, '0');

	return `${year}-${month}-${day}`;
};

export const convertTo24Hour = (time: string): string => {
	const [timePart, modifier] = time.split(' ');
	const [hours, minutes] = timePart.split(':');

	let hoursNumber = parseInt(hours, 10);
	const minutesNumber = parseInt(minutes, 10);
	if (modifier === 'PM' && hoursNumber !== 12) {
		hoursNumber += 12;
	} else if (modifier === 'AM' && hoursNumber === 12) {
		hoursNumber = 0;
	}
	const formattedHours = hoursNumber.toString().padStart(2, '0');
	const formattedMinutes = minutesNumber.toString().padStart(2, '0');
	return `${formattedHours}:${formattedMinutes}`;
};

const TIME_OPTION_MINUTE_STEP = 5;

export const generateTimeOptions = (minTime?: string): DropdownOptions[] => {
	const times: DropdownOptions[] = [];
	let minHours = 0;
	let minMinutes = 0;

	if (minTime) {
		const [time, period] = minTime.split(' ');
		const [hoursStr, minutesStr] = time.split(':');
		let hours = parseInt(hoursStr, 10);
		const minutes = parseInt(minutesStr, 10);

		if (period.toUpperCase() === 'PM' && hours !== 12) {
			hours += 12;
		} else if (period.toUpperCase() === 'AM' && hours === 12) {
			hours = 0;
		}

		minHours = hours;
		minMinutes = minutes;
	}

	for (let hours = 0; hours < 24; hours++) {
		for (let minutes = 0; minutes < 60; minutes += TIME_OPTION_MINUTE_STEP) {
			if (minTime && (hours < minHours || (hours === minHours && minutes < minMinutes))) {
				continue;
			}

			const timeString24 = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
			const hours12 = (hours % 12 || 12).toString().padStart(2, '0');
			const ampm = hours < 12 ? 'AM' : 'PM';
			const timeString12 = `${hours12}:${String(minutes).padStart(2, '0')} ${ampm}`;
			times.push({ id: timeString24, value: timeString12 });
		}
	}
	return times;
};

export const formatDateDDMMYY = (date: Date): string => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');

	return `${day}-${month}-${year}`;
};

export const formatDateMonthDayYear = (date: Date): string => {
	return date.toLocaleDateString('en-US', {
		month: 'long',
		day: 'numeric',
		year: 'numeric',
	});
};

export const getLast90DaysRange = (date: Date): FormateDate => {
	const end = new Date(date);
	const start = new Date(date);
	start.setDate(start.getDate() - 90);
	return { start, end };
};

export const roundToNearestQuarterHour = (date: Date): Date => {
	const newDate = new Date(date);
	const minutes = newDate.getMinutes();
	const seconds = newDate.getSeconds();

	let roundedMinutes: number;
	if (minutes % 15 === 0 && seconds === 0) {
		roundedMinutes = (minutes + 15) % 60;
	} else {
		roundedMinutes = Math.ceil((minutes + 1) / 15) * 15;
	}

	if (roundedMinutes === 60) {
		newDate.setHours(newDate.getHours() + 1);
		roundedMinutes = 0;
	}
	newDate.setMinutes(roundedMinutes);
	newDate.setSeconds(0);
	newDate.setMilliseconds(0);

	return newDate;
};

export const formatTime = (date: Date): string => {
	const hours = date.getHours() % 12 || 12;
	const minutes = date.getMinutes();
	const ampm = date.getHours() >= 12 ? 'PM' : 'AM';
	return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${ampm}`;
};

export const calculateEndTimeAndDate = (startTime: string, startDate: Date): { endTime: string; endDate: Date } => {
	const [time, period] = startTime.split(' ');
	const [hoursStr, minutesStr] = time.split(':');
	let hours = parseInt(hoursStr, 10);
	const minutes = parseInt(minutesStr, 10);

	if (period?.toLowerCase() === 'pm' && hours !== 12) {
		hours += 12;
	} else if (period?.toLowerCase() === 'am' && hours === 12) {
		hours = 0;
	}

	const endDate = new Date(startDate);
	let endHours = hours + 1;

	if (endHours >= 24) {
		endHours -= 24;
		endDate.setDate(endDate.getDate() + 1);
	}

	const endPeriod = endHours >= 12 ? 'PM' : 'AM';
	endHours = endHours % 12 || 12;

	const endTime = `${String(endHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${endPeriod}`;
	return { endTime, endDate };
};

/** Parses time to minutes since midnight. Supports `HH:mm` (24h) and `hh:mm AM/PM` (12h). */
export const parseTimeToMinutes = (time: string): number | null => {
	if (!time?.trim()) return null;
	const trimmed = time.trim();

	const ampmMatch = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
	if (ampmMatch) {
		let hours = parseInt(ampmMatch[1], 10);
		const minutes = parseInt(ampmMatch[2], 10);
		if (Number.isNaN(hours) || Number.isNaN(minutes) || minutes < 0 || minutes > 59) return null;

		const periodUpper = ampmMatch[3].toUpperCase();
		if (periodUpper === 'PM' && hours !== 12) {
			hours += 12;
		} else if (periodUpper === 'AM' && hours === 12) {
			hours = 0;
		}
		if (hours < 0 || hours > 23) return null;
		return hours * 60 + minutes;
	}

	const h24Match = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
	if (h24Match) {
		const hours = parseInt(h24Match[1], 10);
		const minutes = parseInt(h24Match[2], 10);
		if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
		if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
		return hours * 60 + minutes;
	}

	return null;
};

export function isTimePast(currentTime: string, apiTime: string): boolean {
	const currentMinutes = parseTimeToMinutes(currentTime);
	const apiMinutes = parseTimeToMinutes(apiTime);
	if (currentMinutes == null || apiMinutes == null) return false;
	return apiMinutes < currentMinutes;
}

export function convertTime(input: string | null | undefined): string {
	// Parse the input time
	if (!input) return 'N/A';
	const [hours, minutes] = input.split(':').map(Number);

	// If minutes are 45 or less, round down to the nearest 15-minute interval
	if (minutes <= 55) {
		const roundedMinutes = Math.floor(minutes / 5) * 5;
		const formattedMinutes = roundedMinutes.toString().padStart(2, '0');
		const period = hours >= 12 ? 'PM' : 'AM';
		const displayHours = hours % 12 || 12; // Convert to 12-hour format

		return `${displayHours.toString().padStart(2, '0')}:${formattedMinutes} ${period}`;
	}
	// If minutes are greater than 45, round up to the next hour
	else {
		const nextHour = (hours + 1) % 24;
		const period = nextHour >= 12 ? 'PM' : 'AM';
		const displayHours = nextHour % 12 || 12; // Convert to 12-hour format

		return `${displayHours.toString().padStart(2, '0')}:00 ${period}`;
	}
}

export const formatDateForQuery = (date: Date): string => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	const hours = String(date.getHours()).padStart(2, '0');
	const minutes = String(date.getMinutes()).padStart(2, '0');
	const seconds = String(date.getSeconds()).padStart(2, '0');

	return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

export const formatDateString = (dateString: string, includeTime: boolean = true): string => {
	const date = new Date(dateString.replace('Z', ''));
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	const formatted = `${year}-${month}-${day}`;

	if (includeTime) {
		const hours = String(date.getHours()).padStart(2, '0');
		const minutes = String(date.getMinutes()).padStart(2, '0');
		const seconds = String(date.getSeconds()).padStart(2, '0');
		return `${formatted}T${hours}:${minutes}:${seconds}`;
	}

	return formatted;
};

// Date format date with time "2026-04-14 15:23:40"
const IST_OFFSET_MINUTES = -330;

export const formatDateTimeWithOffset = (date: Date, offsetMinutes: number): string => {
	const adjusted = new Date(date);
	adjusted.setMinutes(adjusted.getMinutes() + offsetMinutes);
	const datePart = [
		adjusted.getFullYear(),
		String(adjusted.getMonth() + 1).padStart(2, '0'),
		String(adjusted.getDate()).padStart(2, '0'),
	].join('-');
	const timePart = [
		String(adjusted.getHours()).padStart(2, '0'),
		String(adjusted.getMinutes()).padStart(2, '0'),
		String(adjusted.getSeconds()).padStart(2, '0'),
	].join(':');
	return `${datePart} ${timePart}`;
};

export const formatDateTimeWithIstOffset = (date: Date): string => {
	return formatDateTimeWithOffset(date, IST_OFFSET_MINUTES);
};

export const convertUtcToIst = (utcDateTime: string): string => {
	const date = new Date(utcDateTime);
	if (Number.isNaN(date.getTime())) return '';
	const formatter = new Intl.DateTimeFormat('en-GB', {
		timeZone: 'Asia/Kolkata',
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
		hour12: false,
	});
	const parts = formatter.formatToParts(date);
	const get = (type: Intl.DateTimeFormatPartTypes): string => parts.find((part) => part.type === type)?.value ?? '';
	return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')}:${get('second')}`;
};

// This will give the start and end date of the day
export const getDayBounds = (date: Date): { startDate: Date; endDate: Date } => {
	const startDate = new Date(date);
	startDate.setHours(0, 0, 0, 0);
	const endDate = new Date(date);
	endDate.setHours(23, 59, 59, 999);
	return { startDate, endDate };
};

export const formatInstantAsIstOffsetIso = (instant: Date | number): string => {
	const d = typeof instant === 'number' ? new Date(instant) : instant;
	if (Number.isNaN(d.getTime())) {
		return '';
	}
	const formatter = new Intl.DateTimeFormat('en-CA', {
		timeZone: 'Asia/Kolkata',
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
		hour12: false,
	});
	const parts = formatter.formatToParts(d);
	const y = parts.find((p) => p.type === 'year')?.value;
	const mo = parts.find((p) => p.type === 'month')?.value;
	const day = parts.find((p) => p.type === 'day')?.value;
	let h = parts.find((p) => p.type === 'hour')?.value;
	const mi = parts.find((p) => p.type === 'minute')?.value;
	const s = parts.find((p) => p.type === 'second')?.value;
	if (!y || !mo || !day || h === undefined || !mi || s === undefined) {
		return '';
	}
	if (h === '24') {
		h = '00';
	}
	return `${y}-${mo}-${day}T${h}:${mi}:${s}+05:30`;
};

export const isBetween1stAnd15th = (): boolean => {
	const today = new Date();
	const day = today.getDate();
	const month = today.getMonth();
	const isInFirst15Days = day >= 1 && day <= 15;

	const isQuarterStartMonth = month % 3 === 0;

	return isInFirst15Days && isQuarterStartMonth;
};

export function formatDateOnly(date: Date): string {
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, '0');
	const d = String(date.getDate()).padStart(2, '0');
	return `${y}-${m}-${d}`;
}

/** Ensures API receives `HH:mm` (e.g. `"15:30"`) from TimeSelectDropdown values. */
export function normalizeTimeHHmm(time24: string): string {
	const trimmed = time24.trim();
	const parts = trimmed.split(':');
	const h = parts[0] ?? '0';
	const min = parts[1] ?? '0';
	return `${h.padStart(2, '0')}:${min.padStart(2, '0')}`;
}

/** Normalize RxDB/API time strings to `HH:mm` for TimeSelectDropdown. */
export function timeStoredToHHmm(t: string): string {
	if (!t) return '';
	const [h = '0', rawMin = '0'] = t.trim().split(':');
	const min = String(rawMin).slice(0, 2);
	return `${h.padStart(2, '0')}:${min.padStart(2, '0')}`;
}

/** Local start/end from API date + time strings (YYYY-MM-DD + HH:mm). */
export function meetingDateTimeFromParts(
	dateStr: string | null | undefined,
	timeStr: string | null | undefined
): Date | null {
	if (!dateStr || !timeStr) return null;
	const ymd = String(dateStr)
		.split('T')[0]
		?.match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (!ymd) return null;
	const y = Number(ymd[1]);
	const mo = Number(ymd[2]) - 1;
	const d = Number(ymd[3]);
	const hhmm = timeStoredToHHmm(timeStr);
	if (!hhmm) return null;
	const [h, min] = hhmm.split(':').map((x) => parseInt(x, 10));
	if (Number.isNaN(h) || Number.isNaN(min)) return null;
	return new Date(y, mo, d, h, min, 0, 0);
}

/** Local midnight of today (for date pickers `minDate`, “today or later” checks, etc.). */
export const getStartOfToday = (): Date => getDayBounds(new Date()).startDate;

export function parseIsoDateString(s: string): Date | null {
	const d = new Date(s);
	return Number.isNaN(d.getTime()) ? null : d;
}

/** Formats `HH:mm` (or `hh:mm AM/PM`) for TimeSelectDropdown display without requiring a matching option. */
export function formatTimeValueToLabel(time: string): string {
	if (!time?.trim()) return '';
	const trimmed = time.trim();
	if (/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.test(trimmed)) {
		const match = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
		if (!match) return trimmed;
		return `${String(match[1]).padStart(2, '0')}:${match[2]} ${match[3].toUpperCase()}`;
	}

	const minutes = parseTimeToMinutes(trimmed);
	if (minutes === null) return trimmed;

	const hours = Math.floor(minutes / 60);
	const mins = minutes % 60;
	const hours12 = (hours % 12 || 12).toString().padStart(2, '0');
	const ampm = hours < 12 ? 'AM' : 'PM';
	return `${hours12}:${String(mins).padStart(2, '0')} ${ampm}`;
}

/** Format stored time (`HH:mm` or `hh:mm AM/PM`) to 12-hour display without rounding. */
export function formatActualTime(input: string | null | undefined): string {
	if (!input?.trim()) return 'N/A';
	const totalMinutes = parseTimeToMinutes(input.trim());
	if (totalMinutes === null) return 'N/A';
	const hours = Math.floor(totalMinutes / 60);
	const minutes = totalMinutes % 60;
	const period = hours >= 12 ? 'PM' : 'AM';
	const displayHours = (hours % 12 || 12).toString().padStart(2, '0');
	return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
}
